/** Utilities for modifying view transitions based on route and label */
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { Logger, ViewTransitionStateService } from '@frontend/vanilla/core';

import { EXCLUDED_ROUTES, VIEW_TRANSITION_LOG_TAG as LOG_TAG, TRANSITION_MODIFIERS, TRANSITION_PATHS } from './view-transition.constants';

export function isOverlayClose(): boolean {
    return isPathInNavigation(TRANSITION_PATHS.OVERLAY_PATH, { oldOnly: true });
}

export function isExcludedRoute(): boolean {
    return EXCLUDED_ROUTES.some((route) => isPathInNavigation(route));
}

export function isHorizontalSlideNavigation(): boolean {
    if (isAccountMenuSubNavigation()) {
        return true;
    }

    return isEventNavigation() && !isEventSwitch() && !isProductSwitchTransition();
}

export function isAccountMenuOpenOrCloseNavigation(): boolean {
    return isAccountMenuNavigation() && !isAccountMenuSubNavigation();
}

export function getHorizontalSlideModifiers(): string[] {
    return isEventNavigation()
        ? [TRANSITION_MODIFIERS.EXCLUDE_BOTTOM_NAV, TRANSITION_MODIFIERS.EXCLUDE_BETSLIP, TRANSITION_MODIFIERS.EXCLUDE_QUICK_BET]
        : [];
}

export function isProductSwitchTransition(): boolean {
    return inject(ViewTransitionStateService).isProductSwitch || isAccountMenuOpenOrCloseNavigation();
}

export function isAccountMenuSubNavigation(): boolean {
    const isMenuInFirstRoute = isAccountMenuNavigation({ old: true });
    const isMenuInSecondRoute = isAccountMenuNavigation({ new: true });

    return isMenuInFirstRoute && isMenuInSecondRoute;
}

function isAccountMenuNavigation(options?: PathInNavigationOptions): boolean {
    return (
        isPathInNavigation(TRANSITION_PATHS.ACCOUNT_MENU_PATH, options) ||
        isPathInNavigation(TRANSITION_PATHS.ACCOUNT_MENU_OVERLAY_PATH, options) ||
        isPathInNavigation(TRANSITION_PATHS.MYACCOUNT_PATH, options) ||
        isPathInNavigation(TRANSITION_PATHS.ACCOUNT_SETTINGS_PATH, options)
    );
}

// Indicates if the current navigation is an event switch, e.g. /event-1 -> /event-2, and not /event-1 -> /event-1(overlay:menu)
function isEventSwitch(): boolean {
    return (
        (isPathInNavigation(TRANSITION_PATHS.EVENT_PATH, { both: true }) && !isPathInNavigation(TRANSITION_PATHS.ACCOUNT_MENU_OVERLAY_PATH)) ||
        isPathInNavigation(TRANSITION_PATHS.EVENT_OVERLAY_PATH, { both: true })
    );
}

function isEventNavigation(): boolean {
    return isPathInNavigation(TRANSITION_PATHS.EVENT_PATH) || isPathInNavigation(TRANSITION_PATHS.EVENT_OVERLAY_PATH);
}

type PathInNavigationOptions = {
    old?: boolean;
    new?: boolean;
    both?: boolean;
    oldOnly?: boolean;
    newOnly?: boolean;
    xor?: boolean;
};

function isPathInNavigation(path: string, options?: PathInNavigationOptions): boolean {
    const logger = inject(Logger);
    const router = inject(Router);
    const currentNavigation = router.getCurrentNavigation();

    if (!currentNavigation) {
        logger.error(`${LOG_TAG} isPathInNavigation called outside the context of an active navigation`);

        return false;
    }

    const currentUrl = currentNavigation.finalUrl?.toString();
    const previousUrl = currentNavigation.previousNavigation?.finalUrl?.toString();

    if (options?.old) {
        return isInOldPath(previousUrl, currentUrl, path);
    }

    if (options?.new) {
        return isInNewPath(previousUrl, currentUrl, path);
    }

    if (options?.oldOnly) {
        return isOnlyInOldPath(previousUrl, currentUrl, path);
    }

    if (options?.newOnly) {
        return isOnlyInNewPath(previousUrl, currentUrl, path);
    }

    if (options?.both) {
        return isInBothPaths(previousUrl, currentUrl, path);
    }

    if (options?.xor) {
        return isInEitherPathExclusive(previousUrl, currentUrl, path);
    }

    return isInEitherPath(previousUrl, currentUrl, path);
}

type PathInNavigationFn = (previousUrl: string | undefined, currentUrl: string | undefined, path: string) => boolean;

function createPathInNavigationCheckFn(predicate: (previous: boolean, current: boolean) => boolean): PathInNavigationFn {
    return (previousUrl, currentUrl, path) => {
        const inPrevious = Boolean(previousUrl?.includes(path));
        const inCurrent = Boolean(currentUrl?.includes(path));

        return predicate(inPrevious, inCurrent);
    };
}

const isInOldPath = createPathInNavigationCheckFn((prev, _) => prev);
const isInNewPath = createPathInNavigationCheckFn((_, curr) => curr);
const isOnlyInOldPath = createPathInNavigationCheckFn((prev, curr) => prev && !curr);
const isOnlyInNewPath = createPathInNavigationCheckFn((prev, curr) => curr && !prev);
const isInBothPaths = createPathInNavigationCheckFn((prev, curr) => prev && curr);
const isInEitherPath = createPathInNavigationCheckFn((prev, curr) => prev || curr);
const isInEitherPathExclusive = createPathInNavigationCheckFn((prev, curr) => (prev && !curr) || (curr && !prev));
